import React, {useEffect} from 'react';
import {Link, useParams, useSearchParams} from 'react-router-dom';
import {useAppDispatch, useAppSelector} from '../store/hooks';
import {ADMIN_ACTIVITY_PAGE_SIZE} from '../domain/admin';
import {
  adminUserConversionsApiSelector,
  adminUserConversionsPageSelector,
  adminUserDetailSelector,
} from '../store/selectors/admin.selectors';
import {fetchAdminUserConversions, fetchAdminUserDetail} from '../store/slices/adminThunks';
import {ConversionTable} from './admin/AdminUserActivity';
import {EmptyState, ErrorState, LoadingState, Pagination, Panel} from './admin/AdminPrimitives';
import {ArrowRightIcon, RefreshIcon} from './Icons';

/**
 * `/admin/users/:userId/conversions` — one user's full, paged conversion history at its own URL,
 * split out of the user detail page the same way the global transcription queue is a page rather
 * than a tab: a list worth linking to or refreshing directly needs a URL that says so.
 * `AdminRecentConversions` on the detail page is the preview; this is the rest of it.
 *
 * Offset lives in the URL (`?offset=`), not local state, so the page is directly bookmarkable and
 * survives a refresh mid-page — same pattern as `adminQueue.container.tsx`.
 *
 * The user's name/email is fetched here too rather than assumed present: this page is a valid
 * direct link on its own, not only reachable by clicking through the user detail page first.
 */
const AdminUserConversionsContainer: React.FC = () => {
  const dispatch = useAppDispatch();
  const {userId} = useParams<{userId: string}>();
  const [searchParams, setSearchParams] = useSearchParams();

  const profile = useAppSelector(adminUserDetailSelector);
  const page = useAppSelector(adminUserConversionsPageSelector);
  const api = useAppSelector(adminUserConversionsApiSelector);

  const offset = Math.max(0, Number(searchParams.get('offset') ?? 0) || 0);

  useEffect(() => {
    if (userId) {
      dispatch(fetchAdminUserDetail(userId));
    }
  }, [dispatch, userId]);

  useEffect(() => {
    if (userId) {
      dispatch(fetchAdminUserConversions({userId, limit: ADMIN_ACTIVITY_PAGE_SIZE, offset}));
    }
  }, [dispatch, userId, offset]);

  const setOffset = (next: number) => {
    setSearchParams(
      (previous) => {
        const params = new URLSearchParams(previous);
        if (next > 0) {
          params.set('offset', String(next));
        } else {
          params.delete('offset');
        }
        return params;
      },
      {replace: true}
    );
  };

  if (!userId) {
    return <ErrorState message="No user id in the URL." />;
  }

  const rows = page?.items ?? [];
  const refetch = () => dispatch(fetchAdminUserConversions({userId, limit: ADMIN_ACTIVITY_PAGE_SIZE, offset}));

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-xs text-gray-500">
        <Link to="/admin/users" className="text-indigo-400 hover:text-indigo-300">
          Users
        </Link>
        <ArrowRightIcon className="h-3 w-3" />
        <Link to={`/admin/users/${userId}`} className="text-indigo-400 hover:text-indigo-300">
          {profile?.user.name || profile?.user.email || userId}
        </Link>
        <ArrowRightIcon className="h-3 w-3" />
        <span className="text-gray-300">Conversions</span>
      </div>

      <Panel
        padded={false}
        title="Conversions"
        subtitle="Free and unlimited — every XML this user has generated, newest first."
        actions={
          <button
            type="button"
            onClick={refetch}
            disabled={api.status === 'pending'}
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-700 px-2.5 py-1
             text-[11px] font-medium text-gray-300 transition-colors hover:border-gray-600
             hover:bg-gray-800 disabled:opacity-40 cursor-pointer"
          >
            <RefreshIcon className={`h-3 w-3 ${api.status === 'pending' ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        }
      >
        {api.status === 'rejected' && rows.length === 0 && (
          <ErrorState message={api.error ?? 'Conversions could not be loaded.'} onRetry={refetch} />
        )}

        {api.status !== 'rejected' && api.status === 'pending' && rows.length === 0 && <LoadingState />}

        {api.status !== 'pending' && api.status !== 'rejected' && rows.length === 0 && (
          <EmptyState message="No conversions." />
        )}

        {rows.length > 0 && (
          <div className="overflow-x-auto">
            <ConversionTable rows={rows} />
          </div>
        )}

        {page && page.total > 0 && (
          <Pagination
            total={page.total}
            limit={page.limit || ADMIN_ACTIVITY_PAGE_SIZE}
            offset={offset}
            onOffsetChange={setOffset}
            noun="conversions"
            isLoading={api.status === 'pending'}
          />
        )}
      </Panel>
    </div>
  );
};

export default AdminUserConversionsContainer;
